
import * as React from 'react';
import { LayoutSwitcher } from '../layoutSwitcher/LayoutSwitcher';
import { withCurrentItemSet } from '../decorators/selectors';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { Fragment } from '../../utilities/components/Fragment';
import { connectToItemsScope } from '../decorators/connectToItemsScope';
import { resolveItems } from '../../operations/getItems';
import { hasLayoutFacet, preferredLayoutFacet } from '../../facets/ScopeFacets';
import { pack } from '../../models/Facet';
import { Delete } from '../../utilities/Graft';
import {
  IItemsViewLayoutHandler,
  itemsViewBetaControl,
  IItemsViewBaseProps,
  IItemsViewLayoutProps,
  itemsViewLayoutHandlerControl
} from '../../controls/itemsViewBetaControl';
import { itemsViewDetailsLayoutHandler, itemsViewCompactDetailsLayoutHandler } from './layoutHandlers/detailsLayout';
import { itemsViewTilesLayoutHandler } from './layoutHandlers/tilesLayout';
import { Omit } from '../../utilities/Omit';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { IItemCacheStateProps, WithItemCache } from '../withItemCache/WithItemCache';
import { IItemsScopeStateProps, WithItemsScope } from '../itemsScope/ItemsScope';
import { ISetPreferredLayoutPayload, setPreferredLayout } from '../../operations/setPreferredLayout';
import { IItemCache } from '../../models/IItemCacheObserver';
import { findMatch } from '../../utilities/arrays';
import { itemsViewErrorLayoutHandler } from './layoutHandlers/errorLayout';
import { itemsViewEmptyLayoutHandler } from './layoutHandlers/emptyLayout';

const LayoutNotifier = connectToItemsScope()(connectToItemCache(undefined, (dispatch: IItemCacheDispatch, props: {
  itemsScopeItemKey: string;
}) => {
  const {
    itemsScopeItemKey
  } = props;

  return {
    onMount: () => {
      dispatch(resolveItems({
        items: {
          [itemsScopeItemKey]: hasLayoutFacet.pack(true)
        }
      }));
    },
    onUnmount: () => {
      dispatch(resolveItems({
        items: {
          [itemsScopeItemKey]: pack({
            hasLayout: hasLayoutFacet
          }, {
              hasLayout: new Delete<boolean>()
            })
        }
      }));
    }
  };
})(class extends React.PureComponent<{
  onMount: () => void;
  onUnmount: () => void;
}> {
  public render(): JSX.Element {
    return (
      <Fragment>{ this.props.children }</Fragment>
    );
  }

  public componentDidMount(): void {
    this.props.onMount();
  }

  public componentWillUnmount(): void {
    this.props.onUnmount();
  }
}));

class ItemsViewBase extends React.PureComponent<IItemsViewBaseProps> {
  public render(): JSX.Element {
    return (
      <Fragment>{ this.props.children }</Fragment>
    );
  }
}

const HANDLERS: IItemsViewLayoutHandler[] = [
  itemsViewDetailsLayoutHandler,
  itemsViewCompactDetailsLayoutHandler,
  itemsViewTilesLayoutHandler
];

/**
 * Represents the evaluated state of a layout handler.
 * @internal
 */
export type IEvaluatedItemsViewLayoutHandler =
  Omit<IItemsViewLayoutHandler, 'controlHandler'> &
  typeof itemsViewLayoutHandlerControl.returnType &
  {
    setAsPreferredLayout: () => void;
  };

/**
 * @internal
 */
export interface IWithItemsViewStateProps extends IItemCacheStateProps, IItemsScopeStateProps {
  layoutHandlers: IEvaluatedItemsViewLayoutHandler[];
  itemsViewAs: React.ComponentType<IItemsViewBaseProps>;
}

/**
 * @internal
 */
export interface IWithItemsViewProps extends IRenderProps<IWithItemsViewStateProps> {
  itemKey: string | undefined;
}

/**
 * Render prop component which returns the evaluated states of all available layout handlers
 * for a given item or item set.
 * @internal
 */
export class WithItemsView extends React.PureComponent<IWithItemsViewProps> {
  public render(): JSX.Element {
    const {
      itemKey,
      children
    } = this.props;

    return (
      <WithItemsScope>
        { ({ itemsScopeItemKey }: IItemsScopeStateProps) => {
          return (
            <WithItemCache>
              { ({ itemCache, dispatch }: IItemCacheStateProps) => {
                const {
                  evaluatedLayoutHandlers,
                  itemsViewAs
                } = evaluateLayoutHandlers(itemCache, {
                  itemKey,
                  itemsScopeItemKey,
                  itemsViewAs: ItemsViewBase,
                  setPreferredLayout: (payload: ISetPreferredLayoutPayload) => {
                    dispatch(setPreferredLayout(payload));
                  }
                });

                return renderChildrenWithProps<IWithItemsViewStateProps>(children, {
                  layoutHandlers: evaluatedLayoutHandlers,
                  itemsViewAs,
                  itemCache,
                  itemsScopeItemKey,
                  dispatch
                });
              } }
            </WithItemCache>
          );
        } }
      </WithItemsScope>
    );
  }
}

/**
 * @beta
 */
export interface IItemViewBetaProps {
  itemKey: string | undefined;
}

/**
 * Beta version of ItemsView, which uses control handlers to determine the current layout
 * applicable to a given item type.
 *
 * Not yet intended for production use.
 *
 * @beta
 */
export class ItemViewBeta extends React.PureComponent<IItemViewBetaProps> {
  public render(): JSX.Element {
    const {
      itemKey
    } = this.props;

    return (
      <WithItemsView itemKey={ itemKey }>
        { ({
          layoutHandlers,
          itemsViewAs: ItemsViewAs,
          itemsScopeItemKey,
          itemCache
        }: IWithItemsViewStateProps) => {
          let currentLayout: React.ComponentType<IItemsViewLayoutProps> | undefined;
          let currentLayoutHandlerKey: string | undefined;

          const {
            layout: errorLayout
          } = itemsViewLayoutHandlerControl.evaluate(itemsViewErrorLayoutHandler.controlHandler)(itemCache, {
            itemKey,
            itemsScopeItemKey,
            layoutHandlerKey: itemsViewErrorLayoutHandler.key
          });

          const {
            layout: emptyLayout
          } = itemsViewLayoutHandlerControl.evaluate(itemsViewEmptyLayoutHandler.controlHandler)(itemCache, {
            itemKey,
            itemsScopeItemKey,
            layoutHandlerKey: itemsViewErrorLayoutHandler.key
          });

          for (const {
            layout,
            key: layoutHandlerKey
          } of layoutHandlers) {
            if (layout) {
              currentLayout = layout;
              currentLayoutHandlerKey = layoutHandlerKey;
              break;
            }
          }

          const Layout = errorLayout || currentLayout;
          const EmptyLayout = !errorLayout && emptyLayout;

          return (
            <LayoutNotifier>
              <ItemsViewAs
                itemKey={ itemKey }
                layoutHandlerKey={ currentLayoutHandlerKey }
              >
                <LayoutSwitcher layoutKey={ currentLayoutHandlerKey }>
                  {
                    Layout ? (
                      <Layout key='layout' itemKey={ itemKey } />
                    ) : null
                  }
                </LayoutSwitcher>
              </ItemsViewAs>
              {
                EmptyLayout ? (
                  <EmptyLayout key='empty' itemKey={ itemKey } />
                ) : null
              }
            </LayoutNotifier>
          );
        } }
      </WithItemsView>
    );
  }
}

/**
 * @beta
 */
export const ItemsViewBeta = withCurrentItemSet()(ItemViewBeta);

function evaluateLayoutHandlers(itemCache: IItemCache, props: {
  setPreferredLayout: (payload: ISetPreferredLayoutPayload) => void;
  itemKey: string | undefined;
  itemsScopeItemKey: string;
  itemsViewAs: React.ComponentType<IItemsViewBaseProps>;
}): {
    evaluatedLayoutHandlers: IEvaluatedItemsViewLayoutHandler[];
    itemsViewAs: React.ComponentType<IItemsViewBaseProps>;
  } {
  const {
    itemKey,
    itemsScopeItemKey,
    setPreferredLayout: boundSetPreferredLayout
  } = props;

  const preferredLayout = itemCache.demandItemFacet(preferredLayoutFacet, itemsScopeItemKey);

  const preferredLayoutHandlerKey = preferredLayout && preferredLayout.layoutHandlerKey;

  const controlHandler = itemCache.demandItemFacet(itemsViewBetaControl, itemKey);

  const {
    layoutHandlers: initialLayoutHandlers,
    layoutControlHandler: singleLayoutControlHandler,
    itemsViewAs
  } = itemsViewBetaControl.evaluate(controlHandler)(itemCache, {
    itemKey: itemKey,
    itemsScopeItemKey: itemsScopeItemKey,
    itemsViewAs: props.itemsViewAs,
    layoutHandlers: HANDLERS
  });

  const match = findMatch(
    initialLayoutHandlers,
    (layoutHandler: IItemsViewLayoutHandler) => layoutHandler.key === preferredLayoutHandlerKey);

  const layoutHandlers = match ?
    [match.value, ...initialLayoutHandlers.slice(0, match.index), ...initialLayoutHandlers.slice(match.index + 1)] :
    initialLayoutHandlers;

  const evaluatedLayoutHandlers = layoutHandlers.map((
    {
      controlHandler: layoutControlHandler,
      ...layoutHandler
    }: IItemsViewLayoutHandler
  ): IEvaluatedItemsViewLayoutHandler => {
    const layoutHandlerKey = layoutHandler.key;

    const {
      layout
    } = itemsViewLayoutHandlerControl.evaluate(layoutControlHandler, singleLayoutControlHandler)(itemCache, {
      itemKey,
      itemsScopeItemKey,
      layoutHandlerKey
    });

    return {
      ...layoutHandler,
      layout,
      setAsPreferredLayout: () => {
        boundSetPreferredLayout({
          itemsScopeItemKey,
          layoutHandlerKey: layoutHandlerKey
        });
      }
    };
  });

  return {
    evaluatedLayoutHandlers,
    itemsViewAs
  };
}
